import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trainingApi } from "./api";
import type {
  TrainingApprovalRequest,
  TrainingCategoryCreateRequest,
  TrainingCategoryUpdateRequest,
  TrainingProgramCreateRequest,
  TrainingProgramUpdateRequest,
  TrainingPublishRequest,
  TrainingRequestCreateRequest,
} from "./types";

const KEY = "training-requests";

const CAT_KEY = "training-categories";

export function useTrainingCategories() {
  return useQuery({
    queryKey: [CAT_KEY],
    queryFn: () => trainingApi.categories(),
    staleTime: 1000 * 60 * 10,
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TrainingCategoryCreateRequest) => trainingApi.createCategory(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CAT_KEY] }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: TrainingCategoryUpdateRequest }) =>
      trainingApi.updateCategory(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CAT_KEY] }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => trainingApi.deleteCategory(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CAT_KEY] }),
  });
}

/** There's no dedicated activate/deactivate endpoint on the backend (only a full
 *  PUT /{id} update, which requires name) — so toggling flips `active` through the
 *  same update call, carrying the category's current name/scope along with it. */
export function useToggleCategoryActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, scope, active }: { id: number; name: string; scope?: string | null; active: boolean }) =>
      trainingApi.updateCategory(id, { name, scope, active: !active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CAT_KEY] }),
  });
}

export function useMandatoryTrainings() {
  return useQuery({
    queryKey: [KEY, "mandatory"],
    queryFn: () => trainingApi.mandatoryTrainings(),
    staleTime: 1000 * 60,
  });
}

// ── Categories (all, for admin) ───────────────────────────────────────────────

export function useAllTrainingCategories() {
  return useQuery({
    queryKey: [CAT_KEY, "all"],
    queryFn: () => trainingApi.categoriesAll(),
    staleTime: 1000 * 60 * 5,
  });
}

// ── Programs ──────────────────────────────────────────────────────────────────

const PROG_KEY = "training-programs";

export function useTrainingPrograms(page = 0, size = 15, status?: string, search?: string) {
  return useQuery({
    queryKey: [PROG_KEY, page, size, status, search],
    queryFn: () => trainingApi.programs.list({ page, size, status, search }),
    staleTime: 1000 * 30,
  });
}

export function useTrainingProgram(id: number) {
  return useQuery({
    queryKey: [PROG_KEY, id],
    queryFn: () => trainingApi.programs.get(id),
    enabled: !!id,
  });
}

export function usePublishedPrograms() {
  return useQuery({
    queryKey: [PROG_KEY, "published"],
    queryFn: () => trainingApi.programs.list({ status: "PUBLISHED", size: 20 }),
    staleTime: 1000 * 60,
  });
}

export function useCreateProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TrainingProgramCreateRequest) => trainingApi.programs.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PROG_KEY] }),
  });
}

export function useUpdateProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: TrainingProgramUpdateRequest }) =>
      trainingApi.programs.update(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PROG_KEY] }),
  });
}

export function useDeleteProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => trainingApi.programs.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PROG_KEY] }),
  });
}

export function usePublishProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: TrainingPublishRequest }) =>
      trainingApi.programs.publish(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PROG_KEY] }),
  });
}

export function useCancelProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => trainingApi.programs.cancel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PROG_KEY] }),
  });
}

export function useCompleteProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => trainingApi.programs.complete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PROG_KEY] }),
  });
}

export function useEnrollInProgram() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: TrainingPublishRequest }) =>
      trainingApi.programs.enroll(id, payload),
    onSuccess: (_, { id }) => qc.invalidateQueries({ queryKey: [PROG_KEY, id] }),
  });
}

export function useMarkAttended() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ programId, enrollmentId }: { programId: number; enrollmentId: number }) =>
      trainingApi.programs.markAttended(programId, enrollmentId),
    onSuccess: (_, { programId }) => qc.invalidateQueries({ queryKey: [PROG_KEY, programId] }),
  });
}

export function useMarkAbsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ programId, enrollmentId }: { programId: number; enrollmentId: number }) =>
      trainingApi.programs.markAbsent(programId, enrollmentId),
    onSuccess: (_, { programId }) => qc.invalidateQueries({ queryKey: [PROG_KEY, programId] }),
  });
}

export function useCancelEnrollment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ programId, enrollmentId }: { programId: number; enrollmentId: number }) =>
      trainingApi.programs.cancelEnrollment(programId, enrollmentId),
    onSuccess: (_, { programId }) => qc.invalidateQueries({ queryKey: [PROG_KEY, programId] }),
  });
}

export function useMyTrainingRequests(page = 0, size = 10, status?: string) {
  return useQuery({
    queryKey: [KEY, "mine", page, size, status],
    queryFn: () => trainingApi.mine({ page, size, status }),
    staleTime: 1000 * 30,
  });
}

export function useTrainingInbox(page = 0, size = 10) {
  return useQuery({
    queryKey: [KEY, "inbox", page, size],
    queryFn: () => trainingApi.inbox({ page, size }),
    staleTime: 1000 * 30,
  });
}

export function useAllTrainingRequests(page = 0, size = 10, status?: string, search?: string) {
  return useQuery({
    queryKey: [KEY, "all", page, size, status, search],
    queryFn: () => trainingApi.all({ page, size, status, search }),
    staleTime: 1000 * 30,
  });
}

export function useTrainingRequest(id: number) {
  return useQuery({
    queryKey: [KEY, id],
    queryFn: () => trainingApi.get(id),
    enabled: !!id,
  });
}

export function useCreateTrainingRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TrainingRequestCreateRequest) => trainingApi.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useSubmitTrainingRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => trainingApi.submit(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useApproveTrainingRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload?: TrainingApprovalRequest }) =>
      trainingApi.approve(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useRejectTrainingRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload?: TrainingApprovalRequest }) =>
      trainingApi.reject(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useCancelTrainingRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comments }: { id: number; comments?: string }) =>
      trainingApi.cancel(id, comments),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useUploadTrainingCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) =>
      trainingApi.uploadCertificate(id, file),
    onSuccess: (_, { id }) => qc.invalidateQueries({ queryKey: [KEY, id] }),
  });
}

export function useDeleteTrainingDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, documentId }: { id: number; documentId: number }) =>
      trainingApi.deleteDocument(id, documentId),
    onSuccess: (_, { id }) => qc.invalidateQueries({ queryKey: [KEY, id] }),
  });
}
